import classNames from "classnames";
import FocusTrap from "focus-trap-react";
import uniqueId from "lodash/uniqueId";
import { useCloseDialogOnEscapeKey } from "PFCore/hooks/use_close_dialog_on_escape_key";
import { Id } from "PFTypes";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useClickAway } from "react-use";

import { Tab } from "../tabs";
import { SidePanelBody } from "./body";
import { SidePanelFooter } from "./footer";
import { SidePanelHeader } from "./header";
import { getHeaderOffset } from "./header/side_panel_header.utils";
import { useCloseOnLocationChange } from "./hooks/use_close_on_location_change";
import css from "./side_panel.module.scss";
import { useSidePanelClose } from "./use_side_panel_close";
import { useSidePanelScroll } from "./use_side_panel_scroll";
import { useSidePanelTabs } from "./use_side_panel_tabs";

const BASE_Z_INDEX = 610;

type Size = "default" | "medium" | "large";

export type SidePanelTabs<TAB_ID extends Id> = {
  tabs: Tab<TAB_ID>[];
  defaultSelectedTabId: TAB_ID;
  tabsContent: Record<TAB_ID, React.ReactNode>;
};

export type FooterRendererArgs = {
  onSidePanelClose: () => void;
};

export type SidePanelProps<TAB_ID extends Id = Id> = React.PropsWithChildren<{
  id?: string;
  title?: string | React.ReactNode;
  subTitle?: string;
  actions?: React.ReactNode;
  show: boolean;
  size?: Size;
  onClose?: VoidFunction;
  isCloseDisabled?: boolean;
  classes?: Partial<{ show: string }>;
  fullHeight?: boolean;
  footerRenderer?: (args: FooterRendererArgs) => React.ReactNode;
  footerShadowRecalculateTrigger?: any;
  tabsConfig?: SidePanelTabs<TAB_ID>;
  isOnTop?: boolean;
  zIndex?: number;
  isSidePanelClosing?: boolean;
  noOverflow?: boolean;
  qaId?: string;
  rootRef?: React.RefObject<HTMLDivElement | null>;
  closeOnClickOutside?: boolean;
  noPortal?: boolean;
  disableEntranceAnimation?: boolean;
}>;

const SidePanel = <TAB_ID extends Id>({
  id,
  title,
  subTitle,
  actions,
  show,
  children,
  onClose,
  isCloseDisabled,
  size = "default",
  classes = {},
  fullHeight,
  footerRenderer,
  footerShadowRecalculateTrigger,
  tabsConfig,
  isOnTop = true,
  zIndex = 0,
  isSidePanelClosing = false,
  noOverflow,
  qaId,
  rootRef,
  closeOnClickOutside = false,
  noPortal,
  disableEntranceAnimation = false
}: SidePanelProps<TAB_ID>): React.ReactElement | null => {
  const footerContentRef = useRef<HTMLDivElement>(null);
  const internalRef = useRef<HTMLDivElement>(null);
  const sidePanelRef = rootRef || internalRef;

  const sidePanelId = useMemo(() => id || uniqueId(), [id]);

  const [footerHeight, setFooterHeight] = useState(0);

  const { isClosing, onSidePanelClose } = useSidePanelClose({
    onClose,
    isSidePanelClosing
  });
  useCloseDialogOnEscapeKey({ dialogId: sidePanelId, closeWithAnimation: onSidePanelClose });
  useCloseOnLocationChange({ isOpen: show, onClose: onSidePanelClose });
  useClickAway(sidePanelRef, () => {
    if (closeOnClickOutside && show) {
      onSidePanelClose();
    }
  });
  const { internalTabsConfig, selectedTabId } = useSidePanelTabs({
    tabsConfig,
    show
  });
  const { bodyRef, hasBorderShadow, hasScroll, handleScroll } = useSidePanelScroll({
    selectedTabId,
    recalculateTrigger: footerShadowRecalculateTrigger
  });

  useEffect(() => {
    setFooterHeight(footerContentRef.current?.offsetHeight ?? 0);
  }, [footerContentRef]);

  if (!show) {
    return null;
  }

  const sidePanelElement = (
    <FocusTrap focusTrapOptions={{ clickOutsideDeactivates: true, initialFocus: false, preventScroll: true }}>
      <div
        id={sidePanelId}
        ref={sidePanelRef}
        data-qa-id={qaId}
        data-side-panel="true"
        role="dialog"
        style={{ zIndex: BASE_Z_INDEX + zIndex }}
        data-dialog-id={sidePanelId}
        className={classNames(
          css.root,
          { [css.closing]: isClosing },
          { [css.animateIn]: !isClosing && !disableEntranceAnimation },
          { [css.noAnimation]: !isClosing && disableEntranceAnimation },
          { [css.sizeDefault]: size === "default" },
          { [css.sizeMedium]: size === "medium" },
          { [css.sizeLarge]: size === "large" },
          { [css.fullHeight]: fullHeight },
          { [css.onTop]: isOnTop },
          { [css.noOverflow]: noOverflow },
          classes.show
        )}
      >
        <SidePanelHeader
          title={title}
          subTitle={subTitle}
          actions={actions}
          onClose={onSidePanelClose}
          tabsConfig={internalTabsConfig}
          isCloseDisabled={isCloseDisabled}
        />
        <SidePanelBody
          ref={bodyRef}
          maxHeight={`calc(100% - ${footerHeight + getHeaderOffset(!!tabsConfig)}px)`}
          onScroll={handleScroll}
          noOverflow={noOverflow}
        >
          {tabsConfig ? tabsConfig.tabsContent[selectedTabId] : children}
        </SidePanelBody>
        {footerRenderer && (
          <SidePanelFooter ref={footerContentRef} hasBorderShadow={hasBorderShadow} hasScroll={hasScroll}>
            {footerRenderer({ onSidePanelClose })}
          </SidePanelFooter>
        )}
      </div>
    </FocusTrap>
  );

  if (noPortal) {
    return sidePanelElement;
  }

  const modalTarget = document.getElementById("modal_region") as HTMLDivElement;

  return createPortal(sidePanelElement, modalTarget);
};

export default SidePanel;
